import { useCallback, useMemo } from 'react';
import { catName, componentList, pct1, type Package } from '@piaip/shared';
import { sx, sxc } from '../sx';
import { useApp } from '../state';
import { api } from '../api';
import {
  BTN_PRIMARY,
  BTN_X,
  CARD,
  Empty,
  PageHead,
  SELECT_CELL,
  SERIF_H,
  TH_S,
  TH_S_R,
} from '../components/ui';
import { useRowEditor } from '../components/useRowEditor';

const COMPONENTS = componentList();

export function Packages() {
  const { engine, snapshot, can, mutate } = useApp();
  const canEdit = can('packages');
  const comp = useMemo(() => engine.compPcts(), [engine]);
  const packages = snapshot.packages ?? [];

  return (
    <div
      style={sx(
        'max-width:1000px;margin:0 auto;padding:20px 16px 60px;display:flex;flex-direction:column;gap:18px',
      )}
    >
      <PageHead
        title="Contract packages"
        note="Name each package and its contractor, then assign work components to it."
        action={
          canEdit ? (
            <button
              type="button"
              onClick={() => void mutate(() => api.createPackage())}
              style={sx(BTN_PRIMARY)}
            >
              + Add package
            </button>
          ) : undefined
        }
      />

      <div style={sxc(CARD, 'overflow-x:auto')}>
        <table style={sx('width:100%;border-collapse:collapse;min-width:520px')}>
          <thead>
            <tr>
              <th style={sx(TH_S)}>Package</th>
              <th style={sx(TH_S)}>Contractor</th>
              <th style={sxc(TH_S_R, 'width:110px')}>Progress</th>
              <th style={sx('border-bottom:1px solid #dbe3e0')} />
            </tr>
          </thead>
          <tbody>
            {packages.map((p) => (
              <PackageRow key={p.id} pkg={p} pct={engine.pkgPct(p.id, comp)} disabled={!canEdit} />
            ))}
          </tbody>
        </table>
        {packages.length === 0 ? (
          <div style={sx('margin-top:14px')}>
            <Empty pad="28px 16px">
              {canEdit ? 'No package has been set up yet.' : 'No package has been set up yet.'}
            </Empty>
          </div>
        ) : null}
      </div>

      <div style={sx(CARD)}>
        <div style={sxc(SERIF_H, 'margin-bottom:14px')}>Assignment of work components</div>
        <div style={sx('overflow-x:auto')}>
          <table style={sx('width:100%;border-collapse:collapse;min-width:620px')}>
            <thead>
              <tr>
                <th style={sx(TH_S)}>Component</th>
                <th style={sxc(TH_S, 'width:110px')}>Category</th>
                <th style={sxc(TH_S_R, 'width:90px')}>Progress</th>
                <th style={sxc(TH_S, 'width:210px')}>Package</th>
              </tr>
            </thead>
            <tbody>
              {COMPONENTS.map((c) => (
                <tr key={c.k}>
                  <td style={sx('padding:6px 8px;border-bottom:1px solid #eef2f1;font-size:13px')}>
                    {c.n}
                  </td>
                  <td
                    style={sx('padding:6px 8px;border-bottom:1px solid #eef2f1;font-size:12px;color:#5a6b68')}
                  >
                    {catName(c.cat)}
                  </td>
                  <td
                    style={sx(
                      "padding:6px 8px;border-bottom:1px solid #eef2f1;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:13px",
                    )}
                  >
                    {pct1(comp[c.k])}%
                  </td>
                  <td style={sx('padding:6px 8px;border-bottom:1px solid #eef2f1')}>
                    <select
                      aria-label={`Package for ${c.n}`}
                      value={(snapshot.assign ?? {})[c.k] ?? ''}
                      disabled={!canEdit}
                      onChange={(e) => void mutate(() => api.assign(c.k, e.target.value))}
                      style={sx(SELECT_CELL)}
                    >
                      <option value="">— unassigned —</option>
                      {packages.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name || 'Unnamed package'}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PackageRow({ pkg, pct, disabled }: { pkg: Package; pct: number; disabled: boolean }) {
  const { mutate } = useApp();
  const save = useCallback(
    async (patch: Partial<Package>) => {
      await mutate(() => api.patchPackage(pkg.id, patch));
    },
    [pkg.id, mutate],
  );
  const { row, set, commit } = useRowEditor(pkg, save);

  return (
    <tr>
      <td style={sx('padding:6px 8px;border-bottom:1px solid #eef2f1')}>
        <input
          type="text"
          aria-label="Package name"
          value={row.name}
          disabled={disabled}
          onChange={(e) => set({ name: e.target.value })}
          onBlur={() => void commit()}
          style={sx(
            'padding:9px;border:1px solid #d3dcda;border-radius:4px;font-size:14px;width:100%;box-sizing:border-box',
          )}
        />
      </td>
      <td style={sx('padding:6px 8px;border-bottom:1px solid #eef2f1')}>
        <input
          type="text"
          aria-label="Contractor"
          value={row.contractor}
          disabled={disabled}
          onChange={(e) => set({ contractor: e.target.value })}
          onBlur={() => void commit()}
          style={sx(
            'padding:9px;border:1px solid #d3dcda;border-radius:4px;font-size:14px;width:100%;box-sizing:border-box',
          )}
        />
      </td>
      <td
        style={sx(
          "padding:6px 8px;border-bottom:1px solid #eef2f1;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:13px",
        )}
      >
        {pct1(pct)}%
      </td>
      <td style={sx('padding:6px 8px;border-bottom:1px solid #eef2f1')}>
        <button
          type="button"
          aria-label="Remove this package"
          disabled={disabled}
          onClick={() => {
            if (window.confirm(`Remove ${row.name || 'this package'} and clear its assignments?`)) {
              void mutate(() => api.deletePackage(pkg.id));
            }
          }}
          style={sx(BTN_X)}
        >
          ×
        </button>
      </td>
    </tr>
  );
}
